import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, GraduationCap, XCircle } from 'lucide-react';

import {
  getComprehensionTest,
  readOptions,
  submitComprehensionAttempt,
  type ComprehensionAttemptResult,
} from '@/api/sops.api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useToast } from '@/hooks/useToast';

/**
 * The employee-facing comprehension test (ADR-040).
 *
 * Replaces the plain "I have read and understood" button whenever the SOP has
 * an ACTIVE test: passing writes the acknowledgment itself, and the server
 * refuses a bare acknowledge while the test is active.
 *
 * The taker payload never contains `correctOptionId`, so this component
 * cannot leak the answers even in the browser. Explanations arrive only in the
 * graded response.
 */
export function ComprehensionTestTaker({ slug, onPassed }: { slug: string; onPassed?: () => void }) {
  const qc = useQueryClient();
  const { show } = useToast();
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [result, setResult] = useState<ComprehensionAttemptResult | null>(null);
  // Captured once so the record reflects how long the person actually spent.
  const startedAt = useRef(new Date().toISOString());

  const testQuery = useQuery({
    queryKey: ['sop-comprehension', slug],
    queryFn: () => getComprehensionTest(slug),
    enabled: Boolean(slug),
  });

  const test = testQuery.data;
  const questions = useMemo(() => test?.questions ?? [], [test]);

  const explanationById = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const e of result?.explanations ?? []) map.set(e.questionId, e.explanation);
    return map;
  }, [result]);

  const mutation = useMutation({
    mutationFn: () =>
      submitComprehensionAttempt(
        slug,
        Object.entries(answers).map(([questionId, optionId]) => ({ questionId, optionId })),
        startedAt.current,
      ),
    onSuccess: async (res) => {
      setResult(res);
      if (res.passed) {
        // The pass wrote the acknowledgment, so anything reading ack state is
        // now stale.
        await qc.invalidateQueries({ queryKey: ['my-acks'] });
        show(`Passed with ${String(res.scorePercent)}% — acknowledgment recorded.`, {
          variant: 'success',
        });
        onPassed?.();
      } else {
        show(`Scored ${String(res.scorePercent)}% — below the pass mark. Review and try again.`, {
          variant: 'warning',
        });
      }
    },
    onError: () => {
      show('Could not submit the test.', { variant: 'danger' });
    },
  });

  if (testQuery.isLoading) return <Card className="p-5 text-kw-muted">Loading test…</Card>;
  if (!test?.active) return null;

  const allAnswered = questions.length > 0 && questions.every((q) => answers[q.id] !== undefined);
  const passed = result?.passed === true;

  return (
    <Card className="space-y-4 p-5 print:hidden">
      <div>
        <h2 className="flex items-center gap-2 text-[15px] font-semibold text-kw-shark">
          <GraduationCap className="size-4 text-kw-cerulean-dark" /> Comprehension test required
        </h2>
        <p className="mt-1 max-w-prose text-[13px] text-kw-muted">
          This SOP asks you to demonstrate understanding rather than just acknowledge it. Score{' '}
          {test.passThresholdPercent}% or higher and your acknowledgment is recorded automatically.
          {test.maxAttempts !== null
            ? ` You have ${String(test.maxAttempts)} attempt(s) for this revision.`
            : ''}
        </p>
      </div>

      {passed ? (
        <div className="border-kw-success-text/30 bg-kw-success-bg flex items-start gap-2 rounded-kw-md border p-3">
          <CheckCircle2 className="text-kw-success-text mt-0.5 size-4 shrink-0" />
          <p className="text-kw-success-text text-[13px]">
            Passed with {result.scorePercent}%. Your acknowledgment has been recorded as a signed record.
          </p>
        </div>
      ) : null}

      <ol className="space-y-4">
        {questions.map((q, qi) => {
          const options = readOptions(q.optionsJson);
          const explanation = explanationById.get(q.id);
          return (
            <li key={q.id} className="space-y-2">
              <p className="text-[14px] font-medium text-kw-shark">
                {qi + 1}. {q.prompt}
              </p>
              <div className="space-y-1.5">
                {options.map((opt) => (
                  <label key={opt.id} className="flex items-start gap-2 text-[13px] text-kw-shark">
                    <input
                      type="radio"
                      name={`q-${q.id}`}
                      className="mt-0.5"
                      disabled={passed}
                      checked={answers[q.id] === opt.id}
                      onChange={() => {
                        setAnswers((prev) => ({ ...prev, [q.id]: opt.id }));
                      }}
                    />
                    <span>{opt.text}</span>
                  </label>
                ))}
              </div>
              {/* Explanations exist only after grading — before that the server
                  never sends them, so they cannot give the answer away. */}
              {result && explanation != null && explanation !== '' ? (
                <p className="rounded-kw-md bg-kw-bg-blue px-3 py-2 text-[12px] text-kw-muted">
                  {explanation}
                </p>
              ) : null}
            </li>
          );
        })}
      </ol>

      {result && !result.passed ? (
        <div className="flex items-start gap-2 rounded-kw-md border border-kw-warning-text/30 bg-kw-warning-bg p-3">
          <XCircle className="mt-0.5 size-4 shrink-0 text-kw-warning-text" />
          <p className="text-[13px] text-kw-warning-text">
            Scored {result.scorePercent}% on attempt {result.attemptNumber}. Re-read the SOP above and try
            again.
          </p>
        </div>
      ) : null}

      {!passed ? (
        <div className="flex justify-end">
          <Button
            type="button"
            isLoading={mutation.isPending}
            disabled={!allAnswered || mutation.isPending}
            onClick={() => {
              mutation.mutate();
            }}
          >
            Submit answers
          </Button>
        </div>
      ) : null}
      {!allAnswered && !passed ? (
        <p className="text-right text-[12px] text-kw-muted">Answer every question to submit.</p>
      ) : null}
    </Card>
  );
}
